import React, { useEffect } from 'react'
import { useResolvers } from '../../../../context'
import { styled } from 'styled-components'

import { cssPlayerHeightUnit } from '../../../../utility/constants'
import {
	useSpring,
	useChain,
	useSpringRef,
	animated,
	config,
} from '@react-spring/web'
import type { CulminatingMomentOption } from '@mission.io/mission-toolkit'
import { OptionalMarkdown } from '@mission.io/question-views'

const countUpStaringProps = {
	percentage: 0,
	dimmerOpacity: -200, // hack because useSpring can only chain two animations together in this instance, I have no idea why. (works correctly in https://codesandbox.io/s/react-spring-chained-animations-forked-4bdkj?file=/src/styles.css)
}

const slideOutStartingProps = {
	opacity: 0,
	width: '0em',
	fontSize: '0em',
}

type Props = {
	result: ?string,
	proportion: number,
	option: CulminatingMomentOption,
	rowCount: number,
	index: number,
	isCompressed?: boolean,
	onCountUpStart?: ?() => void,
	onCountUpEnd?: ?() => void,
}

const ALPHABET_ARRAY = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('')

const OPTION_RESULT_WIDTH = '5em'

/**
 * Option - React Component which displays an option and does animation when the result gets defined
 *
 * @param  {{
 *	 result: ?string, The result of the culminating moment
 *	 proportion: number, The proportion of the class voting for this option
 *	 option: CulminatingMomentOption, The option (object) for the culminating moment
 *	}} props - the props for the JSX
 * @return React$Node
 *
 */
export default function Option({
	result,
	proportion,
	option,
	rowCount,
	index,
	isCompressed,
	onCountUpStart,
	onCountUpEnd,
}: Props): React$Node {
	const { imageSourceResolver } = useResolvers()

	const slideOutSpringRef = useSpringRef()
	const slideOutSpring = useSpring({
		from: slideOutStartingProps,
		to: {
			opacity: 1,
			width: OPTION_RESULT_WIDTH,
			fontSize: '2em',
		},
		config: { ...config.molasses, friction: 125 },
		ref: slideOutSpringRef,
	})

	const countUpSpringRef = useSpringRef()
	const countUpSpring = useSpring({
		from: countUpStaringProps,
		to: {
			percentage: proportion,
			dimmerOpacity: result === option._id ? 0.01 : 1,
		},
		onStart: onCountUpStart,
		onRest: onCountUpEnd,
		config: { ...config.molasses, friction: 175 },
		ref: countUpSpringRef,
	})
	let imageUrl = option.imageUrl

	useEffect(() => {
		// reset the springs in case we directly into another culminating moment and this is reused
		if (!result) {
			slideOutSpringRef.set(slideOutStartingProps)
			countUpSpringRef.set(countUpStaringProps)
		}
	}, [result, slideOutSpringRef, countUpSpringRef])

	useChain(result ? [slideOutSpringRef, countUpSpringRef] : [])

	return (
		<OptionWrapper key={`image_${option._id}`}>
			<OptionDisplay>
				<OptionPrefix>
					{ALPHABET_ARRAY[index % ALPHABET_ARRAY.length]}
				</OptionPrefix>
				<Row className="font-game-body">
					{imageUrl && (
						<Image
							src={imageSourceResolver(imageUrl)}
							rowCount={rowCount}
							small={isCompressed}
						/>
					)}
					<PossiblyCenter $center={!imageUrl}>
						<OptionalMarkdown
							interpretAsMarkdown={true}
							className="prose-on-game-light-purple !text-[calc(var(--ph)*3)]">
							{option.text}
						</OptionalMarkdown>
					</PossiblyCenter>
				</Row>
				{/* Spacer so that the result can come in without covering the option text. If the option is compressed,
				we don't have room for the spacer, so it is possible for the result to cover a small part of the text on the far right */}
				{!isCompressed && <CompressedDiv />}
			</OptionDisplay>
			{result ? (
				<>
					<FeedBackWrapper
						style={slideOutSpring}
						className="bg-game-primary-700">
						<animated.span>
							{countUpSpring.percentage.to(p => `${(p * 100).toFixed()}%`)}
						</animated.span>
					</FeedBackWrapper>
					<Dimmer
						style={{
							opacity: countUpSpring.dimmerOpacity,
						}}
					/>
				</>
			) : null}
			<BackGround />
		</OptionWrapper>
	)
}

const BackGround = styled.div`
	background-color: ${({ theme }) => theme.base3}A0;
	position: absolute;
	left: 0;
	right: 0;
	top: 0;
	bottom: 0;
	border-radius: 4px;
	z-index: -1;
`

const FeedBackWrapper = styled(animated.div)`
	display: flex;
	justify-content: center;
	align-items: center;
	position: absolute;
	right: 0;
	top: 0;
	height: 100%;
`

const OptionWrapper = styled.div`
	width: 100%;
	flex: 1;
	position: relative;
	z-index: 1;
	&:not(:last-child) {
		margin-bottom: ${cssPlayerHeightUnit(2)};
	}
`

const Dimmer = styled(animated.div)`
	position: absolute;
	left: 0;
	right: 0;
	top: 0;
	bottom: 0;
	background-color: ${({ theme }) => theme.base4}A0;
	border-radius: 4px;
	z-index: 1;
`

const fontSize = 3 // ph

const OptionDisplay = styled.div.attrs({
	className: 'bg-game-primary-600 overflow-hidden',
})`
	font-size: ${cssPlayerHeightUnit(fontSize)};
	text-align: left;
	border-radius: 4px;
	height: 100%;
	display: flex;
	align-items: center;
	flex-direction: row;
`

const IMAGE_BASE_HEIGHT = 10

const Image = styled.img`
	max-height: ${({ rowCount, small }) =>
		IMAGE_BASE_HEIGHT / (small ? rowCount : rowCount - 1 || 1)}em;
	object-fit: contain;
	margin-right: ${({ theme }) => theme.spacing2x};
`

const OptionPrefix = styled.div.attrs({
	className: 'bg-game-primary-800 font-game-headline h-full px-3',
})`
	display: flex;
	align-items: center;
	justify-content: center;
	margin-right: ${({ theme }) => theme.spacing2x};
`

const Row = styled.div`
	display: flex;
`

const PossiblyCenter = styled.div`
	${({ $center }) => ($center ? `align-self: center;` : '')}
`

const CompressedDiv = styled.div`
	width: ${OPTION_RESULT_WIDTH};
	justify-self: flex-end;
`
